import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";

import { ChatPanelToggleButton } from "./ChatPanelToggleButton";
import { HandRaiseButton } from "./HandRaiseButton";
import { ReactionMenu } from "./ReactionMenu";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { AudioVideoOptionsMenuItems } from "components/footer/AudioVideoOptionsMenu";
import { AIPanelToggleButton } from "components/footer/icon-buttons/AIPanelToggleButton";
import { InstructorNotesPanelToggleButton } from "components/footer/icon-buttons/InstructorNotesPanelToggleButton";
import { UserPanelToggleButton } from "components/footer/icon-buttons/UserPanelToggleButton";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, SubMenu } from "mds/components/Menu";
import {
  useIsMdOrLarger,
  useIsSmOrLarger,
  useIsXlOrLarger,
  useIsXsOrLarger,
} from "mds/hooks/use-responsive";
import { ChevronUpIcon, GearSettingsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSession,
  selectCanStudentsScreenshare,
  selectClassTimerEndsAt,
  selectCurrentClassUser,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPage,
  selectCurrentSectionIsTemplate,
  selectIsDesktopDevice,
  selectScreensharingClassUserId,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { useAv } from "utils/av";
import { useAutoClearReaction } from "utils/hooks/use-auto-clear-reaction";
import { usePresentation } from "utils/presentation";
import { useUserSectionSettings } from "utils/section";

const LazyScreenshareButton = withActiveAv(() => import("components/av/ScreenshareButton"));

type ButtonOrMenuItem = { key: string; button: ReactNode; menuItem: ReactNode | null };

/* Users see the following buttons in this order:
 * 1. Reactions
 * 2. Hand raise
 * 3. Chat
 * 4. Screenshare (students) / Instructor notes (instructors)
 * 5. AI assistant
 * 6. Participants
 */
export const IconButtons = () => {
  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const { canAuthorSection } = useCapabilities();
  const { canSeeAIPanel } = useUserSectionSettings();
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const canStudentsScreenshare = useAppSelector(selectCanStudentsScreenshare);
  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;
  const activeClassSession = !!useAppSelector(selectActiveClassSession);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isDesktopDevice = useAppSelector(selectIsDesktopDevice);
  const selectedPage = useAppSelector(selectCurrentPage);

  const isXsOrLarger = useIsXsOrLarger();
  const isSmOrLarger = useIsSmOrLarger();
  const isMdOrLarger = useIsMdOrLarger();
  const isXlOrLarger = useIsXlOrLarger();
  // Instructors do not see mobile version of footer
  const isMobile = !isMdOrLarger && !canAuthorSection;

  // Run here (rather than in ReactionMenu) so the auto-clear timeout survives ReactionMenu
  // unmounting when it's hidden in the mobile caret menu (which uses `unmountOnClose`).
  useAutoClearReaction();

  const [visibleButtonCount, setVisibleButtonCount] = useState(0);
  const isBreakoutActive = useAppSelector(selectIsInBreakout);
  const isBreakoutsBannerVisible = isInVirtualClass && isBreakoutActive;
  const { presentedPageId } = usePresentation();
  const isPresentationBannerVisible =
    isInClassSession && (screensharingClassUserId || presentedPageId);
  const endTime = useAppSelector(selectClassTimerEndsAt);
  const isTimerBannerVisible = isInClassSession && endTime;
  // At most two banners will be visible at a time (presentation + timer or presentation + breakouts)
  const visibleBannersCount = [
    isBreakoutsBannerVisible,
    isPresentationBannerVisible,
    isTimerBannerVisible,
  ].filter(Boolean).length;

  // TODO: this has only been tested for students
  // TODO: do in a less hardcoded way?
  useEffect(() => {
    if (!activeClassSession) {
      // At most 3 buttons (Instructor notes, AI assistant, Participants) will be visible when no class session is active
      setVisibleButtonCount(3);
      return;
    }

    if (isXlOrLarger) {
      // >= 1280px
      if (canAuthorSection && visibleBannersCount > 1) {
        setVisibleButtonCount(0);
      } else if (canAuthorSection && visibleBannersCount > 0) {
        setVisibleButtonCount(2);
      } else {
        setVisibleButtonCount(6);
      }
    } else if (isMdOrLarger) {
      // >= 850px
      // md and lg breakpoints can be treated the same
      if (canAuthorSection && visibleBannersCount > 0) {
        setVisibleButtonCount(1);
      } else if (visibleBannersCount > 1) {
        setVisibleButtonCount(2);
      } else {
        setVisibleButtonCount(6);
      }
    } else if (isSmOrLarger) {
      // >= 640px
      // Show a max of 8 total buttons on mobile for students
      // Mic, camera, caret menu button, and leave class button take up 4 slots
      if (canAuthorSection && visibleBannersCount > 1) {
        setVisibleButtonCount(0);
      } else if (visibleBannersCount > 1) {
        setVisibleButtonCount(2);
      } else if (visibleBannersCount > 0) {
        setVisibleButtonCount(3);
      } else {
        setVisibleButtonCount(4);
      }
    } else if (isXsOrLarger) {
      // >= 430px
      if (visibleBannersCount > 1) {
        setVisibleButtonCount(0);
      } else if (visibleBannersCount > 0) {
        setVisibleButtonCount(1);
      } else {
        setVisibleButtonCount(2);
      }
    } else if (activeClassSession) {
      setVisibleButtonCount(0);
    }
  }, [
    isXsOrLarger,
    isSmOrLarger,
    isMdOrLarger,
    isXlOrLarger,
    visibleBannersCount,
    activeClassSession,
    canAuthorSection,
  ]);

  const canSeeReactionButton = isInClassSession;
  const canSeeHandRaiseButton = isVirtualClassActive && isInVirtualClass;
  const canSeeChatButton = isInClassSession;
  const canSeeScreenshareButton =
    !canAuthorSection &&
    isInVirtualClass &&
    currentClassUser?.id !== screensharingClassUserId &&
    canStudentsScreenshare &&
    isDesktopDevice;
  const canSeeInstructorNotesButton = canAuthorSection && selectedPage;
  const canSeeAIButton = canAuthorSection || canSeeAIPanel;
  const canSeeUserButton = !isInTemplateSection;

  const entries = useMemo<ButtonOrMenuItem[]>(() => {
    const list: ButtonOrMenuItem[] = [];
    if (canSeeReactionButton) {
      list.push({
        key: "reactions",
        button: <ReactionMenu isMobile={isMobile} />,
        menuItem: <ReactionMenu isMenuItem />,
      });
    }
    if (canSeeHandRaiseButton) {
      list.push({
        key: "hand",
        button: <HandRaiseButton isMobile={isMobile} />,
        menuItem: <HandRaiseButton isMenuItem />,
      });
    }
    if (canSeeChatButton) {
      list.push({
        key: "chat",
        button: <ChatPanelToggleButton isMobile={isMobile} />,
        menuItem: <ChatPanelToggleButton isMenuItem />,
      });
    }
    if (canSeeScreenshareButton) {
      list.push({
        key: "screen",
        button: <LazyScreenshareButton isMobile={isMobile} />,
        menuItem: <LazyScreenshareButton isMenuItem />,
      });
    }
    if (canSeeInstructorNotesButton) {
      list.push({
        key: "notes",
        button: <InstructorNotesPanelToggleButton isMobile={isMobile} />,
        menuItem: <InstructorNotesPanelToggleButton isMenuItem />,
      });
    }
    if (canSeeAIButton) {
      list.push({
        key: "ai",
        button: <AIPanelToggleButton isMobile={isMobile} />,
        menuItem: <AIPanelToggleButton isMenuItem />,
      });
    }
    if (canSeeUserButton) {
      list.push({
        key: "users",
        button: <UserPanelToggleButton isMobile={isMobile} />,
        menuItem: <UserPanelToggleButton isMenuItem />,
      });
    }
    return list;
  }, [
    canSeeReactionButton,
    canSeeHandRaiseButton,
    canSeeChatButton,
    canSeeScreenshareButton,
    canSeeInstructorNotesButton,
    canSeeAIButton,
    canSeeUserButton,
    isMobile,
  ]);

  const visibleButtons = entries.slice(0, visibleButtonCount);
  const hiddenMenuItems = entries.slice(visibleButtonCount).filter((e) => e.menuItem !== null);

  const MenuButton = (
    <Button
      eventName="Footer icon buttons menu"
      kind="squareLegacy"
      size={isMobile ? "xl" : "l"}
      title={t("footer.footer_options")}
      isSquare
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ChevronUpIcon size={isMobile ? "l" : "m"} />
    </Button>
  );

  return (
    <div className="flex min-w-0 items-center gap-2">
      {visibleButtons.map((entry) => (
        <Fragment key={entry.key}>{entry.button}</Fragment>
      ))}
      {(hiddenMenuItems.length > 0 || (isMobile && isInVirtualClass)) && (
        <Menu
          className="dark-menu"
          direction="top"
          menuButton={MenuButton}
          menuClassName="xs:min-w-[320px] max-xs:!left-3 max-xs:!w-[calc(100vw-24px)]"
          unmountOnClose
        >
          {hiddenMenuItems.map((entry, index) => (
            <Fragment key={entry.key}>
              {entry.menuItem}
              {index < hiddenMenuItems.length - 1 && <MenuDivider />}
            </Fragment>
          ))}
          {isMobile && isInVirtualClass && (
            <>
              {hiddenMenuItems.length > 0 && <MenuDivider />}
              <SubMenu
                label={<IconText iconStart={<GearSettingsIcon />} text={t("av.av_settings")} />}
                withArrow
              >
                <AudioVideoOptionsMenuItems />
              </SubMenu>
            </>
          )}
        </Menu>
      )}
    </div>
  );
};
